import { Application, Blocks } from '@/types/application';
import { ArityScope } from '@/types/arity';
import { Connection } from '@/types/connection';
import { Detail, emptyValue } from '@/types/detail';
import { Layout } from '@/types/layout';
import { Configuration, ListParameter, Metadata } from '@/types/metadata/metadata';
import { Format4Arg, Format4Subroutine, ListCounts, MigrationReport, MintId, ToId } from '@/types/migration';
import { Arg, ReturnType, Subroutine } from '@/types/subroutine';
import { findVariable } from '../application';
import { findEventArgPreset } from '../arguments';
import { listArity } from '../arity';
import { findStartblock, stripEmptyDetails } from '../blocks';
import { toNumericId } from '../number';
import {
  isArray,
  isConfigurationEnum,
  isConfigurationMultiple,
  isEmptyValue,
  isNullOrUndefined,
  isNumber,
  isObject,
  isString,
  isVariable,
  isXYPosition
} from '../typeGuards';
import { removeDetailsIndexes } from './format4';
import { reportChanges } from './report';

const newIdMinter = (countMinted?: () => void): MintId => {
  let lastId = 0;

  return (existingId) => {
    if (isNumber(existingId)) {
      lastId = Math.max(lastId, existingId);
      return existingId;
    }

    countMinted?.();
    return (lastId += 1);
  };
};

export const updateToFormat5 = (application: Application, metadata: Metadata, report: MigrationReport): Application => {
  let convertedIds = 0;
  const toId: ToId = (value) => {
    if (!isNumber(value)) convertedIds += 1;
    return toNumericId(value);
  };

  const fixedApplication: Application = fixOutdatedFormatting(application, report);
  const detailKeys = ['substart', 'eventstart'];
  const { details, layouts } = extractLayouts(fixedApplication.blocks.details, report);
  const subroutines = stripViewports(updateSubroutines(fixedApplication, metadata, report, toId), report);

  // The lists are filled against the migrated subroutines, and read references that are numbers by then.
  const numberedDetails = updateDetailIds(details, detailKeys, toId);
  const normalised = normaliseEnumValues(numberedDetails, fixedApplication.blocks.nodes, metadata, report);

  // An application stamped 4 can still carry index keys, so the 3 -> 4 cleanup is run over its details again.
  const withoutIndexKeys = removeDetailsIndexes(normalised, fixedApplication.blocks.nodes, metadata, report);
  const filled = fillListsToArity(
    withoutIndexKeys,
    updateEventHandlerIds(fixedApplication.blocks.event_handlers, toId),
    fixedApplication.blocks.nodes,
    subroutines,
    metadata,
    report
  );

  const migrated = {
    ...fixedApplication,
    format: 5,
    subroutines,
    folders: fixedApplication.folders?.map((folder) => ({
      ...folder,
      id: toId(folder.id),
      parentId: toId(folder.parentId)
    })),
    signals: fixedApplication.signals?.map((signal) => ({
      ...signal,
      id: toId(signal.id),
      substart: toId(signal.substart)
    })),
    semaphores: fixedApplication.semaphores?.map((semaphore) => ({ ...semaphore, id: toId(semaphore.id) })),
    blocks: {
      ...fixedApplication.blocks,
      connections: updateConnections(fixedApplication.blocks.connections, toId, report),
      details: dropEmptyDetails(filled.details, report),
      layouts: { ...fixedApplication.blocks.layouts, ...layouts },
      event_handlers: filled.eventHandlers
    }
  };

  reportChanges(report, 'Id references', 'text', 'a number', convertedIds);
  return migrated;
};

const fixOutdatedFormatting = (application: Application, report: MigrationReport) => {
  reportChanges(report, 'Task variable scope', 'absent', 'an empty list', application.variables.task ? 0 : 1);

  return {
    ...convertPromptLists(application, report),
    variables: { ...application.variables, task: application.variables.task || [] }
  };
};

const updateSubroutines = (
  application: Application,
  metadata: Metadata,
  report: MigrationReport,
  toId: ToId
): Subroutine[] => {
  let splitArgCount = 0;
  let splitEventArgCount = 0;
  let typedEventArgCount = 0;
  let convertedReturnTypes = 0;
  let mintedReturnTypeIds = 0;

  const subroutines = application.subroutines.map((subroutine) => {
    const startblock = isNullOrUndefined(subroutine.startblock) ? undefined : toId(subroutine.startblock);
    const foundStartblock = findStartblock(subroutine.nodes, application.blocks, metadata);
    reportDanglingBlocks(subroutine, application.blocks, report);
    reportStartblock(subroutine, startblock, foundStartblock, report);

    const args = splitArgs(subroutine, application, metadata, newIdMinter(), report, () => (splitArgCount += 1));
    const eventArgs = subroutine.event_args
      ? splitEventArgs(
          subroutine,
          eventArgTypes(foundStartblock, application.blocks, metadata, eventArgLabels(subroutine)),
          newIdMinter(),
          report,
          {
            split: () => (splitEventArgCount += 1),
            typed: () => (typedEventArgCount += 1)
          }
        )
      : undefined;
    const returnTypes = toReturnTypes(
      subroutine,
      newIdMinter(() => (mintedReturnTypeIds += 1)),
      () => (convertedReturnTypes += 1)
    );

    return {
      ...subroutine,
      startblock: startblock,
      folder: isNullOrUndefined(subroutine.folder) ? undefined : toId(subroutine.folder),
      args: args.args,
      arg_bindings: args.bindings,
      event_args: eventArgs?.args,
      event_arg_bindings: eventArgs?.bindings,
      return_types: returnTypes
    };
  });

  const wasSplit = 'an interface and its bindings';
  reportChanges(report, 'Arguments', 'a label and a variable name', wasSplit, splitArgCount);
  reportChanges(report, 'Event arguments', 'a label and a variable name', wasSplit, splitEventArgCount);
  reportChanges(report, 'Event argument types', 'absent', 'the type the start block declares', typedEventArgCount);
  reportChanges(report, 'Return types', 'a type name', '{label, type}', convertedReturnTypes);
  reportChanges(report, 'Return type ids', 'absent', 'a number', mintedReturnTypeIds);
  return subroutines;
};

// A format 4 return type is either a bare type name or a {label, type} pair, and carries no id either way.
const toReturnTypes = (subroutine: Subroutine, mintId: MintId, countConverted: () => void): ReturnType[] =>
  ((subroutine.return_types ?? []) as unknown as (ReturnType | string)[]).map((returnType) => {
    if (!isString(returnType)) return { ...returnType, id: mintId(returnType.id) };

    countConverted();
    return { id: mintId(), label: '', type: returnType };
  });

const subroutineName = (subroutine: Subroutine) => `${subroutine.label} (${subroutine.id})`;

// Format 4 deleted a block a subroutine could not resolve. It is kept now, so it has to be reported instead.
const reportDanglingBlocks = (subroutine: Subroutine, blocks: Blocks, report: MigrationReport) =>
  subroutine.nodes.forEach((nodeId) => {
    if (blocks.nodes[nodeId]) return;

    report.issues.push({
      location: subroutineName(subroutine),
      item: 'Block reference',
      found: `${nodeId}`,
      issue: 'No such block exists'
    });
  });

// The stored startblock is no longer recomputed on load, so one that disagrees with the blocks is reported.
const reportStartblock = (
  subroutine: Subroutine,
  startblock: number | undefined,
  foundStartblock: number | undefined,
  report: MigrationReport
) => {
  if (startblock === foundStartblock) return;

  report.issues.push({
    location: subroutineName(subroutine),
    item: 'startblock',
    found: isNumber(startblock) ? `${startblock}` : '',
    issue: isNumber(foundStartblock)
      ? `The subroutine starts at block ${foundStartblock}`
      : 'The subroutine has no start block'
  });
};

// An arg that already carries an id has been split before, by an earlier run or by the builder itself.
const isSplitArg = (arg: Format4Arg | Arg | string): arg is Arg => !isString(arg) && isNumber((arg as Arg).id);

// A format 4 arg is a bare variable name, or a label and that name; either way the name is the binding.
const toFormat4Arg = (arg: Format4Arg | string | undefined): Format4Arg | undefined =>
  isString(arg) ? { name: arg, label: '' } : arg;

const argName = (label: string | undefined, index: number) => label || `#${index + 1}`;

/**
 * Splits a format 4 arg list into the declared interface and the variable bindings it was assembled from,
 * taking each type from the bound variable. An argument that cannot be typed is reported, never guessed.
 * Ids are unique within the list, so args and event_args are each numbered from 1.
 */
const splitArgs = (
  subroutine: Subroutine,
  application: Application,
  metadata: Metadata,
  mintArgId: MintId,
  report: MigrationReport,
  countSplit: () => void
): { args: Arg[]; bindings: string[] } => {
  const args: Arg[] = [];
  const bindings: string[] = [];
  const format4Args = subroutine.args as unknown as (Format4Arg | Arg | string)[] | undefined;
  const storedBindings = subroutine.arg_bindings ?? [];

  format4Args?.forEach((format4Arg, index) => {
    // Splitting an already split arg would look for a name that is no longer there and drop its type.
    if (isSplitArg(format4Arg)) {
      args.push({ ...format4Arg, id: mintArgId(format4Arg.id) });
      bindings.push(storedBindings[index] ?? '');
      return;
    }

    const arg = toFormat4Arg(format4Arg);
    const binding = arg?.name || '';
    const type = findVariable(subroutine.id, binding, application, metadata)?.type || '';

    if (!type) {
      report.issues.push({
        location: subroutineName(subroutine),
        item: `Argument ${argName(arg?.label, index)}`,
        found: binding,
        issue: binding
          ? "Could not be typed, its bound variable doesn't exist"
          : 'Could not be typed, no variable bound'
      });
    }

    countSplit();
    args.push({ id: mintArgId(), label: arg?.label || '', type: type });
    bindings.push(binding);
  });

  return { args: args, bindings: bindings };
};

// The label each stored event arg carries, whether an earlier run has already split it or not.
const eventArgLabels = (subroutine: Subroutine): string[] =>
  ((subroutine.event_args ?? []) as unknown as (Format4Arg | Arg | string)[]).map(
    (arg) => (isSplitArg(arg) ? arg.label : toFormat4Arg(arg)?.label) || ''
  );

/**
 * The type the start block declares at each position of the stored list. Presets of the same block disagree on
 * what a position holds, so the types come from the one preset the list corresponds to.
 */
const eventArgTypes = (
  startblock: number | undefined,
  blocks: Blocks,
  metadata: Metadata,
  labels: string[]
): string[] => {
  const nodeType = isNumber(startblock) ? blocks.nodes[startblock]?.type : undefined;
  const presets = metadata.blocks.find((block) => block.type === nodeType)?.event_args;

  return findEventArgPreset(presets, labels)?.args.map((arg) => arg.type) ?? [];
};

/**
 * Splits an event arg list the same way, but takes every type from the start block: the values come from the
 * system, so the metadata declares them and the bound variable is the wrong source. Reread on every run.
 */
const splitEventArgs = (
  subroutine: Subroutine,
  declaredTypes: string[],
  mintArgId: MintId,
  report: MigrationReport,
  counts: { split: () => void; typed: () => void }
): { args: Arg[]; bindings: string[] } => {
  const args: Arg[] = [];
  const bindings: string[] = [];
  const format4Args = subroutine.event_args as unknown as (Format4Arg | Arg | string)[];
  const storedBindings = subroutine.event_arg_bindings ?? [];

  format4Args.forEach((format4Arg, index) => {
    const declared = declaredTypes[index] ?? '';
    const reportType = (label: string | undefined, stored: string) =>
      reportEventArgType(
        subroutineName(subroutine),
        `Event argument ${argName(label, index)}`,
        stored,
        declared,
        report,
        counts.typed
      );

    if (isSplitArg(format4Arg)) {
      reportType(format4Arg.label, format4Arg.type);
      args.push({ ...format4Arg, id: mintArgId(format4Arg.id), type: declared || format4Arg.type });
      bindings.push(storedBindings[index] ?? '');
      return;
    }

    const arg = toFormat4Arg(format4Arg);
    reportType(arg?.label, '');
    counts.split();
    args.push({ id: mintArgId(), label: arg?.label || '', type: declared });
    bindings.push(arg?.name || '');
  });

  return { args: args, bindings: bindings };
};

// A position the start block does not declare is reported, and a type it declares differently is replaced.
const reportEventArgType = (
  location: string,
  item: string,
  stored: string,
  declared: string,
  report: MigrationReport,
  countTyped: () => void
) => {
  if (!declared) {
    report.issues.push({
      location: location,
      item: item,
      found: stored,
      issue: stored
        ? 'The start block declares no event argument in this position'
        : 'Could not be typed, the start block declares no event argument in this position'
    });
    return;
  }

  if (declared === stored) return;

  if (stored) {
    report.changes.push({ location: location, item: item, before: stored, after: declared });
    return;
  }

  countTyped();
};

const convertPromptLists = (newApp: any, report: MigrationReport) => {
  let convertedLists = 0;
  let convertedReferences = 0;

  // Converts existing promptlists to variables
  if (newApp?.promptlists) {
    // Each promptlist type -> channel, global, local, and session
    Object.keys(newApp.promptlists).forEach((promptlistVariableType) => {
      // Each id within the promptlist type
      Object.keys(newApp.promptlists[promptlistVariableType]).forEach((promptlistVariable) => {
        // Each individual promptlist under type/id/
        newApp.promptlists[promptlistVariableType][promptlistVariable].forEach((promptlist: string) => {
          newApp.variables[promptlistVariableType][promptlistVariable][promptlist] = 'promptlist';
          convertedLists += 1;
        });
      });
    });
    delete newApp.promptlists;

    Object.keys(newApp.blocks.details).forEach((detailsKey) => {
      // change existing promptlist references from string to object of name and scope
      const promptlists = newApp.blocks.details[detailsKey].prompt_list;
      promptlists &&
        Object.keys(promptlists).forEach((promptlistKey) => {
          if (isString(promptlists[promptlistKey])) {
            newApp.blocks.details[detailsKey].prompt_list[promptlistKey] = {
              name: promptlists[promptlistKey],
              scope: 'local'
            };
            convertedReferences += 1;
          }
        });
      // change existing prelist references from string to object of name and scope
      const prelists = newApp.blocks.details[detailsKey].pre_list;
      prelists &&
        Object.keys(prelists).forEach((prelistKey) => {
          if (isString(prelists[prelistKey])) {
            newApp.blocks.details[detailsKey].pre_list[prelistKey] = { name: prelists[prelistKey], scope: 'local' };
            convertedReferences += 1;
          }
        });

      // change existing promptlist variable references from string to object of name and scope
      const promptlistVariables = newApp.blocks.details[detailsKey].prompt_list_variable;
      if (isString(promptlistVariables)) {
        newApp.blocks.details[detailsKey].prompt_list_variable = { name: promptlistVariables, scope: 'local' };
        convertedReferences += 1;
      }
    });
  }

  reportChanges(report, 'Prompt lists', 'the promptlists section', 'variables of type promptlist', convertedLists);
  reportChanges(report, 'Prompt list references', 'a name', '{name, scope}', convertedReferences);
  return newApp;
};

/**
 * Numbers every destination, and writes the null an unconnected action left out - stringify drops a dst
 * that is undefined, so the key is missing altogether in the application that was loaded.
 */
const updateConnections = (
  connections: Record<string, Connection[]>,
  toId: ToId,
  report: MigrationReport
): Record<string, Connection[]> => {
  let filledDestinations = 0;

  const updated = Object.fromEntries(
    Object.entries(connections).map(([sourceId, sourceConnections]) => [
      sourceId,
      sourceConnections.map((connection) => {
        const dst = isNullOrUndefined(connection.dst) ? null : toId(connection.dst);
        if (dst === null && connection.dst !== null) filledDestinations += 1;

        return { ...connection, dst: dst };
      })
    ])
  );

  reportChanges(report, 'Unconnected actions', 'no dst key', 'dst: null', filledDestinations);
  return updated;
};

// Lifts the block positions that used to live in details out into their own layouts record.
const extractLayouts = (
  details: Record<string, Detail>,
  report: MigrationReport
): { details: Record<string, Detail>; layouts: Record<string, Layout> } => {
  const layouts: Record<string, Layout> = {};

  const strippedDetails = Object.fromEntries(
    Object.entries(details).map(([nodeId, detail]) => {
      const { _position, ...rest } = detail;
      if (isXYPosition(_position)) {
        layouts[nodeId] = { position: { x: _position.x, y: _position.y } };
      }

      return [nodeId, rest];
    })
  );

  reportChanges(report, 'Block positions', 'details._position', 'blocks.layouts', Object.keys(layouts).length);
  return { details: strippedDetails, layouts: layouts };
};

// The position move leaves a block that has nothing else configured with an object holding no keys.
const dropEmptyDetails = (details: Record<string, Detail>, report: MigrationReport): Record<string, Detail> => {
  const kept = stripEmptyDetails(details);
  const dropped = Object.keys(details).length - Object.keys(kept).length;

  reportChanges(report, 'Block details', 'an empty object', 'removed', dropped);
  return kept;
};

const stripViewports = (subroutines: Subroutine[], report: MigrationReport): Subroutine[] => {
  let dropped = 0;

  const stripped = subroutines.map((subroutine) => {
    if (!('_viewport' in subroutine)) return subroutine;

    dropped += 1;
    const { _viewport, ...rest } = subroutine as Format4Subroutine;
    return rest;
  });

  reportChanges(report, 'Subroutine viewports', 'subroutines._viewport', 'removed', dropped);
  return stripped;
};

const updateDetailIds = (details: Record<string, Detail>, keys: string[], toId: ToId): Record<string, Detail> =>
  Object.fromEntries(
    Object.entries(details).map(([nodeId, detail]) => {
      const updatedDetail = { ...detail };
      keys.forEach((key) => {
        const value = updatedDetail[key];
        if (isString(value) || isNumber(value)) {
          updatedDetail[key] = toId(value);
        }
      });

      return [nodeId, updatedDetail];
    })
  );

/**
 * The old GUI stored a whole metadata option where the engine reads the option's value, so the same key appears
 * both ways across one application. What is left of the option is a copy of metadata that goes stale, so it is
 * reduced to the value it carries. A section's parameters sit in the same detail and a multiple's in each of its
 * rows, so both are followed.
 */
const normaliseEnumValues = (
  details: Record<string, Detail>,
  nodes: Blocks['nodes'],
  metadata: Metadata,
  report: MigrationReport
): Record<string, Detail> => {
  let converted = 0;

  const normalised = Object.fromEntries(
    Object.entries(details).map(([nodeId, detail]) => {
      const nodeType = nodes[nodeId]?.type;
      const configurations = metadata.blocks.find((block) => block.type === nodeType)?.configuration;

      return [
        nodeId,
        normaliseDetail(detail, configurations as Configuration[] | undefined, `${nodeId} - ${nodeType}`, {
          count: () => (converted += 1),
          report
        })
      ];
    })
  );

  reportChanges(report, 'Enum values', 'a metadata option', 'the value it carries', converted);
  return normalised;
};

const normaliseDetail = (
  detail: Detail,
  configurations: Configuration[] | undefined,
  location: string,
  counting: { count: () => void; report: MigrationReport }
): Detail => {
  let normalised = detail;

  configurations?.forEach((configuration) => {
    const stored = normalised[configuration.value];

    if (isConfigurationMultiple(configuration)) {
      if (!isArray(stored)) return;

      const rows = stored.map((row) =>
        isObject(row) ? normaliseDetail(row, configuration.sections as Configuration[], location, counting) : row
      );
      if (rows.some((row, index) => row !== stored[index])) {
        normalised = { ...normalised, [configuration.value]: rows };
      }
      return;
    }

    if (!isConfigurationEnum(configuration)) return;

    let value = stored;
    if (isObject(stored)) {
      if (!isString(stored.value) && !isNumber(stored.value)) {
        counting.report.issues.push({
          location: location,
          item: configuration.label,
          found: JSON.stringify(stored),
          issue: 'Not an option of this parameter'
        });
        return;
      }

      value = stored.value;
      counting.count();
      normalised = { ...normalised, [configuration.value]: value };
    }

    const index = configuration.options?.findIndex((option) => option.value === value) ?? -1;
    const section = index >= 0 ? configuration.sections?.[index] : undefined;
    if (section) normalised = normaliseDetail(normalised, section as Configuration[], location, counting);
  });

  return normalised;
};

/**
 * The engine no longer fills these lists in on load, so each one has to exist and hold one entry per row of the
 * interface it is bound to. What makes a parameter arity bound is its type, never its name, and the metadata
 * declares such parameters in two places: a block's configuration, and the args of each of its event handlers.
 */
const fillListsToArity = (
  details: Record<string, Detail>,
  eventHandlers: Blocks['event_handlers'],
  nodes: Blocks['nodes'],
  subroutines: Subroutine[],
  metadata: Metadata,
  report: MigrationReport
): { details: Record<string, Detail>; eventHandlers: Blocks['event_handlers'] } => {
  const counts: ListCounts = { created: 0, padded: 0, truncated: 0 };
  const filledDetails = { ...details };
  const filledHandlers = eventHandlers && { ...eventHandlers };
  const scope: ArityScope = { subroutines, nodes, metadata };

  Object.entries(nodes).forEach(([nodeId, node]) => {
    const metaBlock = metadata.blocks.find((block) => block.type === node.type);
    const location = `${nodeId} - ${node.type}`;

    metaBlock?.configuration.forEach((configuration) => {
      const filled = fillList(configuration, filledDetails[nodeId], toNumericId(nodeId), location, {
        scope,
        counts,
        report
      });
      if (filled) filledDetails[nodeId] = filled;
    });

    // A handler carries its own interface reference, so each one is filled against the subroutine it names.
    metaBlock?.event_handlers?.forEach((eventHandler) =>
      eventHandler.args.forEach((arg) => {
        const handler = filledHandlers?.[nodeId]?.[eventHandler.name];
        if (!handler) return;

        const filled = fillList(arg, handler, toNumericId(nodeId), `${location} ${eventHandler.name}`, {
          scope,
          counts,
          report
        });
        if (filled && filledHandlers) {
          filledHandlers[nodeId] = { ...filledHandlers[nodeId], [eventHandler.name]: filled };
        }
      })
    );
  });

  const lists = 'Argument and result lists';
  reportChanges(report, lists, 'absent', 'a list of the interface arity', counts.created);
  reportChanges(report, lists, 'shorter than the interface', 'padded with empty rows', counts.padded);
  reportChanges(report, lists, 'longer than the interface', 'truncated to the interface', counts.truncated);
  return { details: filledDetails, eventHandlers: filledHandlers };
};

// Returns the container with its list at the interface arity, or nothing when there is no such list to fill.
const fillList = <T extends object>(
  parameter: ListParameter,
  container: T | undefined,
  nodeId: number,
  location: string,
  { scope, counts, report }: { scope: ArityScope; counts: ListCounts; report: MigrationReport }
): T | undefined => {
  const values = container as Record<string, unknown> | undefined;
  const arity = listArity(parameter, values, nodeId, scope);
  if (isNullOrUndefined(arity)) return;

  const stored = values?.[parameter.value];
  const list = isArray(stored) ? stored : undefined;
  if (list?.length === arity) return;

  if (!list) {
    counts.created += 1;
    !isNullOrUndefined(stored) &&
      reportRow(report, location, parameter.label, stored, `Not a list, replaced by ${arity} empty rows`);
  } else if (list.length < arity) {
    counts.padded += 1;
  } else {
    counts.truncated += 1;
    list
      .slice(arity)
      .forEach((dropped, index) =>
        reportRow(
          report,
          location,
          `${parameter.label} ${arity + index + 1}`,
          dropped,
          `Dropped, the interface has ${arity} rows`
        )
      );
  }

  const kept = list?.slice(0, arity) ?? [];
  return {
    ...container,
    [parameter.value]: [...kept, ...Array.from({ length: arity - kept.length }, emptyRow)]
  } as T;
};

const emptyRow = (): emptyValue => ({ empty: true });

const reportRow = (report: MigrationReport, location: string, item: string, row: unknown, issue: string) =>
  report.issues.push({ location: location, item: item, found: describeRow(row), issue: issue });

const describeRow = (row: unknown): string => {
  if (isEmptyValue(row)) return '(empty)';
  if (isVariable(row)) return `${row.scope}.${row.name}`;
  return isObject(row) ? JSON.stringify(row) : `${row}`;
};

const updateEventHandlerIds = (eventHandlers: Blocks['event_handlers'], toId: ToId): Blocks['event_handlers'] =>
  eventHandlers &&
  Object.fromEntries(
    Object.entries(eventHandlers).map(([nodeId, handlers]) => [
      nodeId,
      Object.fromEntries(
        Object.entries(handlers).map(([eventName, handler]) => [
          eventName,
          { ...handler, eventstart: toId(handler.eventstart) }
        ])
      )
    ])
  );
